import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    TextField,
    Button,
    Tooltip,
    Typography,
    CircularProgress,
    FormControl,
    Select,
    MenuItem,
    InputAdornment,
    Chip,
    Badge,
    Alert,
    Autocomplete,
    IconButton,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    FilterList as FilterIcon,
    CalendarToday as CalendarIcon,
    Business as BusinessIcon,
    Person as PersonIcon,
    PriorityHigh as PriorityHighIcon,
    Refresh as RefreshIcon,
    Clear as ClearIcon,
} from '@mui/icons-material';
import FilterListAltIcon from '@mui/icons-material/FilterListAlt';
import QuizIcon from '@mui/icons-material/Quiz';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, subDays, startOfDay, parseISO } from 'date-fns';
import Brandcrumb from '../../../../componentsUiBlock/Brandcrumb';
import DeleteConfirmDialog from '../../../../componentsUiBlock/DeleteConfirmDialog';
import { ApiResponse, Employee, Enquiry, FilterParams, Party, Product } from '../../types';
import { request } from '../../../../utils/request';
import { useDebounce } from '../../../../utils/useDebounce';
import { toast } from 'react-toastify';

export const getStatusIconBall = (status?: string) => {
    switch (status) {
        case 'NEW': return '🟢';
        case 'ASSIGNED': return '🔵';
        case 'IN_PROGRESS': return '🟡';
        case 'FOLLOW_UP': return '🟠';
        case 'CONVERTED': return '🟣';
        case 'CLOSED': return '⚫';
        case 'CANCELLED': return '🔴';
        default: return '⚪';
    }
};

const ListEnquiry = () => {
    const navigate = useNavigate();
    const location = useLocation();

    // State for enquiries
    const [enquiries, setEnquiries] = useState<Enquiry[]>([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);

    // State for filter data
    const [parties, setParties] = useState<Party[]>([]);
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [, setProducts] = useState<Product[]>([]);

    // Get logged-in user info
    const loginUser = JSON.parse(localStorage.getItem('loginUser') || '{}');
    const loggedInUserId = loginUser?.userId;
    const loggedInUserRole = loginUser?.role?.name?.toLowerCase();

    // Check if logged-in user is admin
    const isAdmin = loggedInUserRole === 'admin';

    // State for employee filter
    const [employeeFilter, setEmployeeFilter] = useState<Employee | null>(null);
    const [isEmployeeFilterDisabled, setIsEmployeeFilterDisabled] = useState(false);

    // Other filter states
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [statusFilter, setStatusFilter] = useState<string>('ALL');
    const [priorityFilter, setPriorityFilter] = useState<string>('ALL');
    const [partyFilter, setPartyFilter] = useState<Party | null>(null);
    const [productFilter, setProductFilter] = useState<Product | null>(null);
    const [dateRange, setDateRange] = useState<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null,
    });
    const [quickDateFilter, setQuickDateFilter] = useState<string>('ALL');

    // Flag to track if filters from location state have been applied
    const [filtersApplied, setFiltersApplied] = useState(false);

    // Sorting and pagination
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [orderBy, setOrderBy] = useState<string>('enquiry_date');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [total, setTotal] = useState(0);
    const [, setTotalPages] = useState(0);

    // Delete dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
    const [itemToDelete, setItemToDelete] = useState<Enquiry | null>(null);
    const [loadingDelete, setLoadingDelete] = useState(false);

    // Check location state for pre-set filters
    const checkLocationState = useCallback(() => {
        const state = location.state as {
            partyId?: string;
            employeeId?: string;
            source?: string;
            status?: string;
            startDate?: string;
            endDate?: string;
        };
        return {
            partyId: state?.partyId,
            employeeId: state?.employeeId,
            source: state?.source,
            status: state?.status,
            startDate: state?.startDate,
            endDate: state?.endDate
        };
    }, [location.state]);

    // Apply filters from location state
    const applyLocationFilters = useCallback(() => {
        const locationState = checkLocationState();

        if (locationState.source === 'dashboard' && !filtersApplied) {
            // Apply status filter
            if (locationState.status && locationState.status !== 'ALL') {
                setStatusFilter(locationState.status);
            }

            // Apply date range filter
            if (locationState.startDate && locationState.endDate) {
                try {
                    const start = parseISO(locationState.startDate);
                    const end = parseISO(locationState.endDate);
                    setDateRange({ start, end });
                    setQuickDateFilter('ALL');
                } catch (error) {
                    console.error('Error parsing dates:', error);
                }
            }

            setFiltersApplied(true);
        }
    }, [checkLocationState, filtersApplied]);

    // Build query params from filters
    const buildQueryParams = useCallback((): FilterParams => {
        const params: FilterParams = {
            page: page + 1,
            limit: rowsPerPage,
            sortBy: orderBy,
            sortOrder: order,
        };

        if (debouncedSearch) params.search = debouncedSearch;
        if (statusFilter !== 'ALL') params.status = statusFilter;
        if (priorityFilter !== 'ALL') params.priority = priorityFilter;
        if (partyFilter) params.party_id = partyFilter._id;

        // Use employee filter - if not admin and employee filter is set, use that
        // Otherwise if not admin but no filter set, don't send employee filter (backend will handle)
        if (employeeFilter) {
            params.assigned_employee_id = employeeFilter._id;
        } else if (!isAdmin && !employeeFilter) {
            // For non-admin users, filter by their own employee record
            const loggedInEmployee = employees.find(e => e.userId === loggedInUserId);
            if (loggedInEmployee) {
                params.assigned_employee_id = loggedInEmployee._id;
            }
        }

        if (productFilter) params.product_id = productFilter._id;

        // Handle date filters
        if (dateRange.start || dateRange.end) {
            if (dateRange.start) params.startDate = format(dateRange.start, 'yyyy-MM-dd');
            if (dateRange.end) params.endDate = format(dateRange.end, 'yyyy-MM-dd');
        } else if (quickDateFilter !== 'ALL') {
            const today = new Date();
            let startDate: Date;

            switch (quickDateFilter) {
                case 'TODAY':
                    startDate = startOfDay(today);
                    break;
                case 'YESTERDAY':
                    startDate = startOfDay(subDays(today, 1));
                    break;
                case 'LAST_7_DAYS':
                    startDate = subDays(today, 7);
                    break;
                case 'LAST_30_DAYS':
                    startDate = subDays(today, 30);
                    break;
                case 'THIS_MONTH':
                    startDate = new Date(today.getFullYear(), today.getMonth(), 1);
                    break;
                default:
                    startDate = startOfDay(today);
            }
            params.startDate = format(startDate, 'yyyy-MM-dd');
            params.endDate = format(today, 'yyyy-MM-dd');
        }

        return params;
    }, [debouncedSearch, statusFilter, priorityFilter, partyFilter, employeeFilter,
        productFilter, dateRange, quickDateFilter, page, rowsPerPage, orderBy, order, isAdmin, employees, loggedInUserId]);

    // Fetch filter data
    const fetchFilterData = useCallback(async () => {
        try {
            // Fetch parties
            const partiesResponse = await request({
                url: 'party/get-all-parties',
                method: 'get',
            });
            const partiesData: Party[] = partiesResponse.parties || [];
            setParties(partiesData);

            // Fetch employees
            const employeesResponse = await request({
                url: 'employee/get-all-employees',
                method: 'get',
            });
            const employeesData: Employee[] = employeesResponse.data || [];
            setEmployees(employeesData);

            // Find logged-in user's employee record
            const loggedInEmployee = employeesData.find(e => e.userId === loggedInUserId);

            // For non-admin users, set employee filter to their own record and disable the filter
            if (!isAdmin && loggedInEmployee) {
                setEmployeeFilter(loggedInEmployee);
                setIsEmployeeFilterDisabled(true);
            } else {
                setIsEmployeeFilterDisabled(false);
            }

            // Fetch products
            const productsResponse = await request({
                url: 'product/get-all-products',
                method: 'get',
            });
            const productsData: Product[] = productsResponse.products || [];
            setProducts(productsData);

            // Check location state and set filters
            const locationState = checkLocationState();

            if (locationState.partyId) {
                const foundParty = partiesData.find((p: Party) => p._id === locationState.partyId);
                if (foundParty) {
                    setPartyFilter(foundParty);
                }
            }

            if (locationState.employeeId && isAdmin) {
                // Only set employee filter from location state if admin
                const foundEmployee = employeesData.find((e: Employee) => e._id === locationState.employeeId);
                if (foundEmployee) {
                    setEmployeeFilter(foundEmployee);
                }
            }

            // Apply dashboard filters after data is loaded
            applyLocationFilters();
        } catch (error) {
            console.error('Error fetching filter data:', error);
        }
    }, [checkLocationState, loggedInUserId, isAdmin, applyLocationFilters]);

    // Fetch enquiries with filters
    const fetchEnquiries = useCallback(async () => {
        setLoading(true);
        try {
            const params = buildQueryParams();
            const queryString = new URLSearchParams(params as any).toString();

            const response = await request({
                url: `enquiry/get-all-enquiries?${queryString}`,
                method: 'get',
            }) as ApiResponse;

            if (response.success) {
                setEnquiries(response.data || []);
                if (response.pagination) {
                    setTotal(response.pagination.total);
                    setTotalPages(response.pagination.totalPages);
                } else {
                    setTotal(response.data?.length || 0);
                }
            }
        } catch (error) {
            console.error('Error fetching enquiries:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [buildQueryParams]);

    // Fetch data on component mount
    useEffect(() => {
        fetchFilterData();
    }, [fetchFilterData]);

    // Fetch enquiries when filters change
    useEffect(() => {
        fetchEnquiries();
    }, [fetchEnquiries]);

    // Clear filters and location state
    const handleClearFilters = () => {
        setSearch('');
        setStatusFilter('ALL');
        setPriorityFilter('ALL');
        setPartyFilter(null);

        // For non-admin users, reset to their own employee filter
        if (!isAdmin) {
            const loggedInEmployee = employees.find(e => e.userId === loggedInUserId);
            if (loggedInEmployee) {
                setEmployeeFilter(loggedInEmployee);
            } else {
                setEmployeeFilter(null);
            }
        } else {
            setEmployeeFilter(null);
        }

        setProductFilter(null);
        setDateRange({ start: null, end: null });
        setQuickDateFilter('ALL');
        setPage(0);
        setFiltersApplied(false);

        // Clear location state by navigating without state
        navigate('/enquiry', { replace: true });
    };

    // Helper functions
    const getPartyName = (partyId?: string) => {
        if (!partyId) return 'Walk-in Customer';
        const party = parties.find(p => p._id === partyId);
        return party ? party.partyName : 'Unknown Party';
    };

    const getEmployeeName = (employeeId?: string) => {
        if (!employeeId) return 'Not Assigned';
        const employee = employees.find(e => e._id === employeeId);
        return employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown';
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'NEW': return 'default';
            case 'ASSIGNED': return 'info';
            case 'IN_PROGRESS': return 'primary';
            case 'FOLLOW_UP': return 'warning';
            case 'CONVERTED': return 'success';
            case 'CLOSED': return 'secondary';
            case 'CANCELLED': return 'error';
            default: return 'default';
        }
    };

    const getPriorityIcon = (priority?: string) => {
        switch (priority) {
            case 'HIGH': return <PriorityHighIcon color="error" fontSize="small" />;
            case 'MEDIUM': return <PriorityHighIcon color="warning" fontSize="small" />;
            case 'LOW': return <PriorityHighIcon color="success" fontSize="small" />;
            default: return <PriorityHighIcon color="disabled" fontSize="small" />;
        }
    };

    const formatDate = (date: any) => {
        if (!date) return '';
        return format(new Date(date), 'dd/MM/yyyy');
    };

    // Event handlers
    const handleSort = (property: string) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRefresh = () => {
        setRefreshing(true);
        fetchEnquiries();
        fetchFilterData();
    };

    const handleDeleteClick = (enquiry: Enquiry) => {
        setItemToDelete(enquiry);
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        if (!itemToDelete) return;

        setLoadingDelete(true);
        try {
            const res = await request({
                url: `enquiry/delete-enquiry/${itemToDelete._id}`,
                method: 'delete',
            });

            if (res.success) {
                toast.success(res.message || 'Enquiry Deleted');
                await fetchEnquiries();
                setOpenDeleteDialog(false);
                setItemToDelete(null);
            }
        } catch (error: any) {
            console.error('Error deleting enquiry:', error);
            toast.error(error);
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
        setItemToDelete(null);
    };

    // Head cells for table
    const headCells = [
        { id: 'status', label: 'Status', sortable: true, width: 120 },
        { id: 'subject', label: 'Subject', sortable: false, width: 200 },
        { id: 'enquiry_no', label: 'Enquiry No', sortable: true, width: 120 },
        { id: 'priority', label: 'Priority', sortable: true, width: 100 },
        { id: 'enquiry_date', label: 'Date', sortable: true, width: 100 },
        { id: 'party_id', label: 'Party', sortable: false, width: 150 },
        { id: 'assigned_employee_id', label: 'Assigned To', sortable: false, width: 140 },
        { id: 'actions', label: 'Actions', sortable: false, width: 120 },
    ];

    // Check if any filters are active
    const hasActiveFilters = Boolean(
        search ||
        statusFilter !== 'ALL' ||
        priorityFilter !== 'ALL' ||
        partyFilter ||
        employeeFilter ||
        productFilter ||
        dateRange.start ||
        dateRange.end ||
        quickDateFilter !== 'ALL'
    );

    // Get the employee to display in filter (for non-admin users)
    const getDisplayEmployeeFilter = () => {
        if (employeeFilter) return employeeFilter;
        if (!isAdmin) {
            const loggedInEmployee = employees.find(e => e.userId === loggedInUserId);
            if (loggedInEmployee) return loggedInEmployee;
        }
        return null;
    };

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>

            <LocalizationProvider dateAdapter={AdapterDateFns}>
                <Card elevation={2} sx={{ borderRadius: 1 }}>
                    <CardContent sx={{ pb: 2 }}>
                        {/* Header with filters */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => navigate("/enquiry/createEnquiry")}
                                startIcon={<AddIcon />}
                                size="small"
                            >
                                New Enquiry
                            </Button>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                {/* Total count */}
                                <Badge
                                    badgeContent={total}
                                    showZero
                                    color="primary"
                                    sx={{ ml: 'auto' }}
                                >
                                    <QuizIcon color="action" />
                                </Badge>

                                {/* Search */}
                                <TextField
                                    size="small"
                                    placeholder="Search enquiries..."
                                    variant="outlined"
                                    sx={{ width: 180 }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" color="action" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    disabled={loading}
                                />
                                <Tooltip title="Refresh Enquiry" placement='top' arrow>
                                    <span>
                                        <IconButton
                                            size="small"
                                            onClick={handleRefresh}
                                            disabled={refreshing || loading}
                                        >
                                            {refreshing ? <CircularProgress size={20} /> : <RefreshIcon fontSize="small" />}
                                        </IconButton>
                                    </span>
                                </Tooltip>

                                {hasActiveFilters && (
                                    <Tooltip title="Clear Filters" placement='top' arrow>
                                        <IconButton
                                            size="small"
                                            onClick={handleClearFilters}
                                        >
                                            <ClearIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </Box>

                        {/* Accordion */}
                        <Accordion sx={{ my: 2, boxShadow: 'none', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                            <AccordionSummary
                                expandIcon={<FilterListAltIcon />}
                                aria-controls="panel1-content"
                                id="panel1-header"
                                sx={{
                                    '&:hover': { backgroundColor: '#e9eef6' }
                                }}
                            >
                                Filters
                            </AccordionSummary>
                            <AccordionDetails>
                                {/* Compact Filter Section */}
                                <Paper elevation={0} sx={{
                                    p: 1.5,
                                    mb: 2,
                                    backgroundColor: 'background.default',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: 'divider'
                                }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                                        {/* Status Filter */}
                                        <FormControl size="small" sx={{ minWidth: '24%' }}>
                                            <Select
                                                value={statusFilter}
                                                onChange={(e) => setStatusFilter(e.target.value)}
                                                displayEmpty
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <FilterIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Status</MenuItem>
                                                <MenuItem value="NEW">🟢 New</MenuItem>
                                                <MenuItem value="ASSIGNED">🔵 Assigned</MenuItem>
                                                <MenuItem value="IN_PROGRESS">🟡 In Progress</MenuItem>
                                                <MenuItem value="FOLLOW_UP">🟠 Follow Up</MenuItem>
                                                <MenuItem value="CONVERTED">🟣 Converted</MenuItem>
                                                <MenuItem value="CLOSED">⚫ Closed</MenuItem>
                                                <MenuItem value="CANCELLED">🔴 Cancelled</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Priority Filter */}
                                        <FormControl size="small" sx={{ minWidth: '24%' }}>
                                            <Select
                                                value={priorityFilter}
                                                onChange={(e) => setPriorityFilter(e.target.value)}
                                                displayEmpty
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Priority</MenuItem>
                                                <MenuItem value="HIGH"><PriorityHighIcon color="error" />High</MenuItem>
                                                <MenuItem value="MEDIUM"><PriorityHighIcon color="warning" />Medium</MenuItem>
                                                <MenuItem value="LOW"><PriorityHighIcon color="success" />Low</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Party Filter */}
                                        <Autocomplete
                                            size="small"
                                            options={parties}
                                            getOptionLabel={(option) => option.partyName}
                                            value={partyFilter}
                                            onChange={(_, newValue) => setPartyFilter(newValue)}
                                            sx={{ width: '24%' }}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder="Party"
                                                    disabled={loading}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <BusinessIcon fontSize="small" color="action" />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />

                                        {/* Employee Filter */}
                                        <Autocomplete
                                            size="small"
                                            options={employees}
                                            getOptionLabel={(option) => `${option.first_name} ${option.last_name}`}
                                            value={getDisplayEmployeeFilter()}
                                            onChange={(_, newValue) => {
                                                if (!isEmployeeFilterDisabled) {
                                                    setEmployeeFilter(newValue);
                                                }
                                            }}
                                            sx={{ width: '24%' }}
                                            disabled={isEmployeeFilterDisabled || loading}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    placeholder={isEmployeeFilterDisabled ? "Filtered by your employee account" : "Employee"}
                                                    disabled={isEmployeeFilterDisabled || loading}
                                                    InputProps={{
                                                        ...params.InputProps,
                                                        startAdornment: (
                                                            <>
                                                                <InputAdornment position="start">
                                                                    <PersonIcon fontSize="small" color="action" />
                                                                </InputAdornment>
                                                                {params.InputProps.startAdornment}
                                                            </>
                                                        ),
                                                    }}
                                                />
                                            )}
                                        />
                                        {isEmployeeFilterDisabled && (
                                            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                (Filtered to your assigned enquiries)
                                            </Typography>
                                        )}

                                        {/* Quick Date Filters */}
                                        <FormControl size="small" sx={{ minWidth: '24%' }}>
                                            <Select
                                                value={quickDateFilter}
                                                onChange={(e) => setQuickDateFilter(e.target.value)}
                                                displayEmpty
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <CalendarIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                disabled={loading}
                                            >
                                                <MenuItem value="ALL">All Dates</MenuItem>
                                                <MenuItem value="TODAY">Today</MenuItem>
                                                <MenuItem value="YESTERDAY">Yesterday</MenuItem>
                                                <MenuItem value="LAST_7_DAYS">Last 7 Days</MenuItem>
                                                <MenuItem value="LAST_30_DAYS">Last 30 Days</MenuItem>
                                                <MenuItem value="THIS_MONTH">This Month</MenuItem>
                                            </Select>
                                        </FormControl>

                                        {/* Date Range Pickers */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '48.7%' }}>
                                            <DatePicker
                                                label="From"
                                                value={dateRange.start}
                                                onChange={(date) => {
                                                    setDateRange(prev => ({ ...prev, start: date ? ((date as any).toDate ? (date as any).toDate() : new Date(date as any)) : null }))
                                                    setQuickDateFilter('ALL');
                                                }}
                                                disabled={loading}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: { width: '50%' }
                                                    }
                                                }}
                                            />
                                            <Typography variant="body2">to</Typography>
                                            <DatePicker
                                                label="To"
                                                value={dateRange.end}
                                                onChange={(date) => {
                                                    setDateRange(prev => ({ ...prev, end: date ? ((date as any).toDate ? (date as any).toDate() : new Date(date as any)) : null }))
                                                    setQuickDateFilter('ALL');
                                                }}
                                                disabled={loading}
                                                slotProps={{
                                                    textField: {
                                                        size: 'small',
                                                        sx: { width: '50%' }
                                                    }
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                </Paper>
                            </AccordionDetails>
                        </Accordion>

                        {/* Table */}
                        <TableContainer sx={{ maxHeight: 500 }}>
                            <Table stickyHeader size="small">
                                <TableHead>
                                    <TableRow>
                                        {headCells.map((head) => (
                                            <TableCell
                                                key={head.id}
                                                sx={{
                                                    fontWeight: 'bold',
                                                    whiteSpace: 'nowrap',
                                                    width: head.width,
                                                    py: 1
                                                }}
                                            >
                                                {head.sortable ? (
                                                    <TableSortLabel
                                                        active={orderBy === head.id}
                                                        direction={orderBy === head.id ? order : 'asc'}
                                                        onClick={() => handleSort(head.id)}
                                                        sx={{ fontSize: '0.8rem' }}
                                                        disabled={loading}
                                                    >
                                                        {head.label}
                                                    </TableSortLabel>
                                                ) : (
                                                    <Typography variant="caption" fontWeight="bold">
                                                        {head.label}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {loading ? (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center" sx={{ py: 3 }}>
                                                <CircularProgress size={24} />
                                                <Typography variant="body2" sx={{ mt: 1 }}>
                                                    Loading enquiries...
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    ) : enquiries.length > 0 ? (
                                        enquiries.map((enquiry) => (
                                            <TableRow key={enquiry._id} hover sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                                                {/* Status */}
                                                <TableCell sx={{ py: 0 }}>
                                                    <Chip
                                                        label={
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <span>{getStatusIconBall(enquiry.status)}</span>
                                                                <span>{enquiry.status || 'NEW'}</span>
                                                            </Box>
                                                        }
                                                        color={getStatusColor(enquiry.status) as any}
                                                        size="small"
                                                        sx={{
                                                            fontSize: '0.7rem',
                                                            height: 24,
                                                            '& .MuiChip-label': { px: 1 }
                                                        }}
                                                    />
                                                </TableCell>

                                                {/* Subject */}
                                                <TableCell sx={{ py: 1, maxWidth: 200 }}>
                                                    <Tooltip title={enquiry.subject || 'No subject'} arrow>
                                                        <Typography variant="body2" noWrap fontSize="0.8rem">
                                                            {enquiry.subject || 'No subject'}
                                                        </Typography>
                                                    </Tooltip>
                                                </TableCell>

                                                {/* Enquiry Number */}
                                                <TableCell sx={{ py: 1 }}>
                                                    <Typography variant="body2" fontWeight="bold" noWrap>
                                                        {enquiry.enquiry_no}
                                                    </Typography>
                                                </TableCell>

                                                {/* Priority */}
                                                <TableCell sx={{ py: 1 }}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        {getPriorityIcon(enquiry.priority)}
                                                        <Typography variant="body2" fontSize="0.8rem">
                                                            {enquiry.priority || 'MEDIUM'}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Enquiry Date */}
                                                <TableCell sx={{ py: 1 }}>
                                                    <Typography variant="body2" fontSize="0.8rem">
                                                        {formatDate(enquiry.enquiry_date)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Party */}
                                                <TableCell sx={{ py: 1 }}>
                                                    <Box display="flex" alignItems="center" gap={0.5}>
                                                        <BusinessIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                        <Typography variant="body2" noWrap fontSize="0.8rem">
                                                            {getPartyName(enquiry.party_id)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>

                                                {/* Assigned Employee */}
                                                <TableCell sx={{ py: 1 }}>
                                                    <Typography variant="body2" noWrap fontSize="0.8rem">
                                                        {getEmployeeName(enquiry.assigned_employee_id)}
                                                    </Typography>
                                                </TableCell>

                                                {/* Actions */}
                                                <TableCell sx={{ py: 1 }}>
                                                    <Box display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={0.8}>
                                                        <Button
                                                            variant="text"
                                                            fullWidth
                                                            onClick={() => navigate(`/enquiry/${enquiry._id}`)}
                                                            sx={{
                                                                minWidth: 'auto',
                                                                fontSize: '0.60rem',
                                                                textWrap: 'nowrap',
                                                                py: 0.1,
                                                                px: 2,
                                                                borderRadius: 5,
                                                                border: '.8px solid #7f5ee666',
                                                                '&:hover': { backgroundColor: '#7f5ee666' }
                                                            }}
                                                        >
                                                            View / Edit
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            fullWidth
                                                            variant="text"
                                                            color="error"
                                                            onClick={() => handleDeleteClick(enquiry)}
                                                            sx={{
                                                                minWidth: 'auto',
                                                                fontSize: '0.60rem',
                                                                py: 0.1,
                                                                px: 2,
                                                                borderRadius: 5,
                                                                border: '.8px solid #ff4d6e5e',
                                                                '&:hover': { backgroundColor: '#ff4d6e5e' }
                                                            }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={headCells.length} align="center" sx={{ py: 3 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {hasActiveFilters
                                                        ? 'No enquiries match your filters'
                                                        : 'No enquiries found. Create your first enquiry!'}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>

                        {/* Pagination */}
                        <TablePagination
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            component="div"
                            count={total}
                            rowsPerPage={rowsPerPage}
                            page={page}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            sx={{
                                borderTop: '1px solid',
                                borderColor: 'divider',
                                '.MuiTablePagination-toolbar': {
                                    minHeight: 52,
                                    padding: 0,
                                },
                                '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                    fontSize: '0.8rem',
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </LocalizationProvider>

            {/* Delete Confirmation Dialog */}
            <DeleteConfirmDialog
                open={openDeleteDialog}
                loading={loadingDelete}
                title="Delete Enquiry"
                description={
                    <>
                        Are you sure you want to delete the enquiry{' '}
                        <strong>"{itemToDelete?.enquiry_no} - {itemToDelete?.subject}"</strong>?
                        <br /><br />
                        <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>
                            This action cannot be undone. All associated data will be permanently removed.
                        </Alert>
                    </>
                }
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </>
    );
};

export default ListEnquiry;